import { createRouter, createWebHistory } from 'vue-router';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { MIGRATION_ALLOWED_EMAILS } from '@/config/adminEmails';
import { getRoleClaims } from '@/auth/roleClaims';

// Lazy-loaded route components
const Home = () => import(/* webpackChunkName: "home" */ '../views/Home.vue');
const Signup = () => import(/* webpackChunkName: "auth" */ '../views/auth/Signup.vue');
const Admin = () => import(/* webpackChunkName: "auth" */ '../views/auth/Admin.vue');
const Login = () => import(/* webpackChunkName: "auth" */ '../views/auth/Login.vue');
const CreateOffer = () => import(/* webpackChunkName: "offers" */ '../views/offers/CreateOffer.vue');
const OfferList = () => import(/* webpackChunkName: "offers" */ '../views/offers/OfferList.vue');
const DoneList = () => import(/* webpackChunkName: "offers" */ '../views/offers/DoneList.vue');
const DetailsOffer = () => import(/* webpackChunkName: "offers" */ '../views/offers/DetailsOffer.vue');
const MyList = () => import(/* webpackChunkName: "offers" */ '../views/offers/MyList.vue');
const DetailsOrder = () => import(/* webpackChunkName: "orders" */ '../views/orders/DetailsOrder.vue');
const OrdersList = () => import(/* webpackChunkName: "orders" */ '../views/orders/OrdersList.vue');
const CreatePdf = () => import(/* webpackChunkName: "pdf" */ '../views/orders/CreatePdf.vue');
const DataHandler = () => import(/* webpackChunkName: "admin" */ '../views/data/DataHandler.vue');
const ProductionPlan = () => import(/* webpackChunkName: "production" */ '../views/production/ProductionPlan.vue');
const ProductionList = () => import(/* webpackChunkName: "production" */ '../views/production/ProductionList.vue');
const DetailsProduction = () => import(/* webpackChunkName: "production" */ '../views/production/DetailsProduction.vue');
const Teams = () => import(/* webpackChunkName: "admin" */ '../views/admin/Teams.vue');
const Vat = () => import(/* webpackChunkName: "admin" */ '../views/admin/Vat.vue');
const OffersReport = () => import(/* webpackChunkName: "admin" */ '../views/admin/OffersReport.vue');
const MigrationAdmin = () => import(/* webpackChunkName: "admin" */ '../views/admin/MigrationAdmin.vue');
const RealtimeTest = () => import(/* webpackChunkName: "admin" */ '../views/admin/RealtimeTest.vue');
const Statistics = () => import(/* webpackChunkName: "admin" */ '../views/stats/Statistics.vue');
const Dashboard = () => import(/* webpackChunkName: "admin" */ '../views/stats/Dashboard.vue');
const NotFound = () => import(/* webpackChunkName: "common" */ '../views/NotFound.vue');

const getCurrentUser = () => {
  const auth = getAuth();
  return new Promise((resolve, reject) => {
    const unsubscribe = onAuthStateChanged(
      auth,
      user => {
        unsubscribe();
        resolve(user);
      },
      reject
    );
  });
};

// Re-export for backward compatibility with existing importers.
export { MIGRATION_ALLOWED_EMAILS };

const requireAuth = (requiredClaims = [], options = {}) => async (to, from, next) => {
  try {
    const user = await getCurrentUser();
    if (!user) return next('/login');

    // Etap 9 Faza 0: authorization now comes from Postgres via /api/auth/me
    // (cached in roleClaims.js), not from the Firebase token's custom claims.
    // Authentication is still the Firebase session resolved above.
    const claims = await getRoleClaims(user);

    const hasClaim =
      requiredClaims.length === 0 ||
      requiredClaims.some(claim => claims[claim] === true);

    const emailAllowlist = options.emails;
    const hasEmail =
      !emailAllowlist ||
      (user.email && emailAllowlist.includes(user.email.toLowerCase()));

    if (hasClaim && hasEmail) {
      return next();
    } else {
      return next('/login');
    }
  } catch (e) {
    console.error(e);
    return next('/login');
  }
};

const routes = [
  // No auth guard: this is the redirect target for unauthenticated AND
  // role-denied users. Unauthenticated users never see it (App.vue paints the
  // login overlay over the whole app); role-denied users get a clear "no
  // access" screen instead of the bare 404 the catch-all used to serve.
  { path: '/login', name: 'Login', component: Login },
  { path: '/', name: 'Home', component: Home, beforeEnter: requireAuth(['admin', 'sprzedawca', 'konstruktor', 'produkcja']) },
  { path: '/signup', name: 'Signup', component: Signup, beforeEnter: requireAuth(['admin']), meta: { note: 'Only admins can create new accounts' } },
  { path: '/admin', name: 'Admin', component: Admin, beforeEnter: requireAuth(['admin']) },
  { path: '/data/DataHandler', name: 'DataHandler', component: DataHandler, beforeEnter: requireAuth(['admin']) },
  { path: '/stats/Statistics', name: 'Statistics', component: Statistics, beforeEnter: requireAuth(['admin']) },
  { path: '/offers/create', name: 'CreateOffer', component: CreateOffer, beforeEnter: requireAuth(['konstruktor', 'sprzedawca', 'admin']) },
  { path: '/offers/List', name: 'OfferList', component: OfferList, beforeEnter: requireAuth(['konstruktor', 'sprzedawca', 'admin']) },
  { path: '/offers/DoneList', name: 'DoneList', component: DoneList, beforeEnter: requireAuth(['konstruktor', 'sprzedawca', 'admin']) },
  { path: '/offers/MyList', name: 'MyList', component: MyList, beforeEnter: requireAuth(['konstruktor', 'admin']) },
  { path: '/offers/DetailsOffer/:id', name: 'DetailsOffer', component: DetailsOffer, beforeEnter: requireAuth(['konstruktor', 'sprzedawca', 'admin']), props: true },
  { path: '/orders/List', name: 'OrdersList', component: OrdersList, beforeEnter: requireAuth(['admin', 'sprzedawca', 'konstruktor']), props: true },
  { path: '/orders/DetailsOrder/:id', name: 'DetailsOrder', component: DetailsOrder, beforeEnter: requireAuth(['admin', 'sprzedawca', 'konstruktor']), props: true },
  { path: '/production/ProductionList', name: 'ProductionList', component: ProductionList, beforeEnter: requireAuth(['admin', 'produkcja', 'sprzedawca']) },
  { path: '/production/ProductionPlan', name: 'ProductionPlan', component: ProductionPlan, beforeEnter: requireAuth(['konstruktor', 'sprzedawca', 'produkcja', 'admin']) },
  { path: '/production/DetailsProduction/:id', name: 'DetailsProduction', component: DetailsProduction, beforeEnter: requireAuth(['admin', 'sprzedawca', 'produkcja']), props: true },
  { path: '/orders/createpdf', name: 'CreatePdf', component: CreatePdf, beforeEnter: requireAuth(['admin', 'konstruktor', 'sprzedawca']) },
  { path: '/admin/teams', name: 'Teams', component: Teams, beforeEnter: requireAuth(['admin', 'sprzedawca']) },
  { path: '/admin/vat', name: 'Vat', component: Vat, beforeEnter: requireAuth(['admin', 'sprzedawca']) },
  { path: '/admin/offers-report', name: 'OffersReport', component: OffersReport, beforeEnter: requireAuth(['admin']) },
  { path: '/admin/migration', name: 'MigrationAdmin', component: MigrationAdmin, beforeEnter: requireAuth(['admin'], { emails: MIGRATION_ALLOWED_EMAILS }) },
  { path: '/admin/realtime-test', name: 'RealtimeTest', component: RealtimeTest, beforeEnter: requireAuth(['admin'], { emails: MIGRATION_ALLOWED_EMAILS }) },
  { path: '/stats/dashboard', name: 'Dashboard', component: Dashboard, beforeEnter: requireAuth(['admin']) },
  { path: '/:catchAll(.*)', name: 'NotFound', component: NotFound }
];

const router = createRouter({
  history: createWebHistory(),
  routes
});

router.onError((error, to) => {
  const msg = error?.message || '';
  const isChunkError =
    error?.name === 'ChunkLoadError' ||
    /Loading chunk \d+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg);

  if (!isChunkError) return;

  const RELOAD_KEY = '__chunk_reload__';
  if (sessionStorage.getItem(RELOAD_KEY)) {
    console.error('[router] Chunk load failed after reload — forcing cache bypass', error);
    sessionStorage.removeItem(RELOAD_KEY);
    const sep = to.fullPath.includes('?') ? '&' : '?';
    window.location.assign(to.fullPath + sep + '_cb=' + Date.now());
    return;
  }
  sessionStorage.setItem(RELOAD_KEY, '1');
  window.location.assign(to.fullPath);
});

router.afterEach(() => {
  sessionStorage.removeItem('__chunk_reload__');
});

export default router;
